import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const SESSION_KEY = "tn-intro-played";
const INTRO_TOTAL_MS = 1100;
const FADE_OUT_AT_MS = 850;

const LogoIntro = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);
  const [fading, setFading] = useState(false);
  const decidedRef = useRef(false);

  // Decide once whether the intro should play (StrictMode-safe via ref guard).
  useEffect(() => {
    if (pathname !== "/") return;
    if (typeof window === "undefined") return;
    if (decidedRef.current) return;
    decidedRef.current = true;

    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      sessionStorage.setItem(SESSION_KEY, "1");
      return;
    }

    sessionStorage.setItem(SESSION_KEY, "1");
    setShow(true);
  }, [pathname]);

  // Schedule the fade/hide timers whenever the intro is shown. Kept in a
  // separate effect so StrictMode's double-invoke always re-arms the timers.
  useEffect(() => {
    if (!show) return;
    const t1 = setTimeout(() => setFading(true), FADE_OUT_AT_MS);
    const t2 = setTimeout(() => setShow(false), INTRO_TOTAL_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: fading ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          style={{ pointerEvents: fading ? "none" : "auto" }}
          className="fixed inset-0 z-[80] bg-[#5C6B49] flex items-center justify-center"
          data-testid="logo-intro"
          aria-hidden="true"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse 50% 35% at 50% 50%, rgba(107,119,83,0.12), transparent 70%)",
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex flex-col items-center"
          >
            <img
              src="/brand/logo-white-1200.png"
              alt="Tobias Nunner"
              width={140}
              height={175}
              draggable={false}
              style={{
                width: "min(36vw, 140px)",
                height: "auto",
                display: "block",
              }}
            />
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.55, delay: 0.25, ease: "easeOut" }}
              style={{ transformOrigin: "center" }}
              className="mt-5 h-px w-24 bg-[#E8E5D0]"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LogoIntro;
